import React, { useEffect, useMemo, useState } from 'react';
import {
  landingDreamsService,
  LandingDream,
  LandingDreamsStats,
} from '../services/landingDreamsService';

const LOCALES = [
  ['', 'Tüm diller'],
  ['tr', 'Türkçe'],
  ['en', 'English'],
  ['de', 'Deutsch'],
  ['es', 'Español'],
  ['fr', 'Français'],
  ['it', 'Italiano'],
  ['pt', 'Português'],
  ['ja', '日本語'],
  ['zh', '中文'],
  ['ar', 'العربية'],
  ['hi', 'हिन्दी'],
] as const;

function formatDate(ts: string) {
  return new Date(ts).toLocaleString('tr-TR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const LandingDreamsPage: React.FC = () => {
  const [stats, setStats] = useState<LandingDreamsStats | null>(null);
  const [items, setItems] = useState<LandingDream[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [q, setQ] = useState('');
  const [locale, setLocale] = useState('');
  const [interpreted, setInterpreted] = useState<'' | 'true' | 'false'>('');
  const [offset, setOffset] = useState(0);
  const limit = 25;

  // Modal
  const [selected, setSelected] = useState<LandingDream | null>(null);

  // Debounced search
  const [debouncedQ, setDebouncedQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  // Reset offset when filters change
  useEffect(() => {
    setOffset(0);
  }, [debouncedQ, locale, interpreted]);

  // Fetch data
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    landingDreamsService
      .list({ limit, offset, q: debouncedQ, locale, interpreted })
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setTotal(res.total);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Yüklenemedi');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQ, locale, interpreted, offset]);

  // Fetch stats once + on filter changes
  useEffect(() => {
    landingDreamsService.stats().then(setStats).catch(() => {});
  }, []);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total]);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Landing Dreams</h1>
        <p className="mt-1 text-sm text-gray-600">
          Site ziyaretçilerinin "Rüyanı yaz, yorumlayalım" formundan gönderdikleri rüyalar.
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          <StatCard label="Toplam" value={stats.total} />
          <StatCard label="Son 24 saat" value={stats.last24h} />
          <StatCard label="Son 7 gün" value={stats.last7d} />
          <StatCard label="Son 30 gün" value={stats.last30d} />
          <StatCard label="Yorum bekleyen" value={stats.pendingInterpretation} accent="orange" />
          <StatCard label="E-posta verdi" value={stats.withEmail} accent="purple" />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rüya / e-posta / sembol ara…"
            className="md:col-span-2 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <select
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {LOCALES.map(([code, label]) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </select>
          <select
            value={interpreted}
            onChange={(e) => setInterpreted(e.target.value as '' | 'true' | 'false')}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Tüm durumlar</option>
            <option value="false">Yorum bekliyor</option>
            <option value="true">Yorumlandı</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading && items.length === 0 ? (
          <div className="p-12 text-center text-gray-500">Yükleniyor…</div>
        ) : error ? (
          <div className="p-12 text-center text-red-500">Hata: {error}</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-gray-500 italic">
            Henüz rüya yok ya da filtreyle eşleşmedi.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <Th>Tarih</Th>
                  <Th>Dil</Th>
                  <Th>Sembol</Th>
                  <Th className="hidden md:table-cell">Rüya</Th>
                  <Th className="hidden lg:table-cell">E-posta</Th>
                  <Th>Durum</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {items.map((d) => (
                  <tr
                    key={d.id}
                    onClick={() => setSelected(d)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-3 text-xs font-mono text-gray-500 whitespace-nowrap">
                      {formatDate(d.capturedAt)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-800 text-xs font-mono">
                        {d.locale}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <span
                        className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-mono whitespace-nowrap inline-block max-w-[200px] overflow-hidden text-ellipsis"
                        title={d.symbolSlug}
                      >
                        {d.symbolSlug}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm italic text-gray-700 hidden md:table-cell max-w-md">
                      <div className="truncate">{d.dreamText.slice(0, 120)}…</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 hidden lg:table-cell">
                      {d.email || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                          d.interpretation
                            ? 'bg-cyan-100 text-cyan-800'
                            : 'bg-pink-100 text-pink-800'
                        }`}
                      >
                        {d.interpretation ? 'Yorumlandı' : 'Bekliyor'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="mt-4 flex justify-center items-center gap-3 text-sm">
          <button
            onClick={() => setOffset(Math.max(0, offset - limit))}
            disabled={offset === 0}
            className="px-3 py-1.5 rounded border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-500 hover:text-primary-600"
          >
            ← Önceki
          </button>
          <span className="text-gray-500">
            {currentPage} / {totalPages}
          </span>
          <button
            onClick={() => setOffset(offset + limit)}
            disabled={offset + limit >= total}
            className="px-3 py-1.5 rounded border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-500 hover:text-primary-600"
          >
            Sonraki →
          </button>
        </div>
      )}

      {/* Modal */}
      {selected && <DreamModal dream={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: number; accent?: 'orange' | 'purple' }> = ({
  label,
  value,
  accent,
}) => {
  const accentClass =
    accent === 'orange'
      ? 'text-orange-600'
      : accent === 'purple'
      ? 'text-purple-600'
      : 'text-gray-900';
  return (
    <div className="bg-white shadow rounded-lg p-4">
      <div className="text-xs text-gray-500 uppercase tracking-wider">{label}</div>
      <div className={`mt-1 text-2xl font-light ${accentClass}`}>{value.toLocaleString('tr-TR')}</div>
    </div>
  );
};

const Th: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <th
    className={`px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${className}`}
  >
    {children}
  </th>
);

const DreamModal: React.FC<{ dream: LandingDream; onClose: () => void }> = ({ dream, onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-start justify-center p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 mt-12"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Rüya · <span className="font-mono text-sm text-amber-700">{dream.symbolSlug}</span>
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            aria-label="Kapat"
          >
            ×
          </button>
        </div>

        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm mb-5">
          <dt className="text-gray-500">Tarih:</dt>
          <dd className="text-gray-700">{formatDate(dream.capturedAt)}</dd>
          <dt className="text-gray-500">Dil:</dt>
          <dd className="text-gray-700">{dream.locale}</dd>
          <dt className="text-gray-500">Sayfa:</dt>
          <dd className="text-gray-700 break-all">
            {dream.pageUrl ? (
              <a
                href={dream.pageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:underline"
              >
                {dream.pageUrl}
              </a>
            ) : (
              '—'
            )}
          </dd>
          <dt className="text-gray-500">E-posta:</dt>
          <dd>
            {dream.email ? (
              <a href={`mailto:${dream.email}`} className="text-primary-600 hover:underline">
                {dream.email}
              </a>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </dd>
          <dt className="text-gray-500">IP:</dt>
          <dd className="text-gray-700 font-mono text-xs">{dream.ip || '—'}</dd>
          <dt className="text-gray-500">User-agent:</dt>
          <dd className="text-gray-500 text-xs break-all">
            {(dream.userAgent || '').slice(0, 200)}
          </dd>
        </dl>

        <div className="bg-purple-50 border-l-4 border-pink-500 rounded p-4 text-gray-800 leading-relaxed whitespace-pre-wrap">
          {dream.dreamText}
        </div>
      </div>
    </div>
  );
};

export default LandingDreamsPage;
